'use client';

import { ArrowDown01Icon, ArrowRight01Icon } from '@terra/ui-icons';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { Collapsible } from '../../../components/collapsible';
import { MetaDefault, MonoSmall } from '../../../components/typography';
import type {
  OobEvidencePayload,
  OobInteraction,
} from './oob-evidence-payload';

export type OobInteractionsCardProps = {
  payload: OobEvidencePayload;
};

const CHEVRON_WIDTH = '14px';
const CHEVRON_SIZE = 10;
const PROTOCOL_WIDTH = '56px';
const SOURCE_WIDTH = '132px';
const RECEIVED_WIDTH = '190px';
const RECEIVED_LOCALE = 'en-GB';
const RECEIVED_FORMAT: Intl.DateTimeFormatOptions = {
  timeZone: 'UTC',
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
};

const receivedAt = (timestamp: string): string => {
  const parsed = new Date(timestamp);

  return Number.isNaN(parsed.getTime())
    ? timestamp
    : `${parsed.toLocaleString(RECEIVED_LOCALE, RECEIVED_FORMAT)} UTC`;
};

const presentText = (value: string | undefined): string | undefined =>
  typeof value === 'string' && value.length > 0 ? value : undefined;

const requestLine = (interaction: OobInteraction): string | undefined => {
  const parts = [interaction.httpMethod, interaction.httpPath].filter(
    (part): part is string => typeof part === 'string' && part.length > 0,
  );

  return parts.length > 0 ? parts.join(' ') : undefined;
};

const capturedRequest = (interaction: OobInteraction): string | undefined =>
  presentText(interaction.rawRequest) ?? requestLine(interaction);

function Transcript({ children }: { children: string }) {
  return (
    <MonoSmall
      display="block"
      color="fg.default"
      whiteSpace="pre-wrap"
      wordBreak="break-all"
    >
      {children}
    </MonoSmall>
  );
}

function Cell({ width, children }: { width?: string; children: string }) {
  return (
    <MonoSmall
      color="fg.default"
      width={width}
      flexShrink={0}
      truncate
      minW={0}
    >
      {children}
    </MonoSmall>
  );
}

function ColumnHeaders({ showQuery }: { showQuery?: boolean }) {
  const t = useTranslations('designSystem.artifacts.oobResult.field');

  return (
    <HStack gap={2} alignItems="center" width="100%">
      <Box width={CHEVRON_WIDTH} flexShrink={0} />
      <MetaDefault color="fg.muted" width={PROTOCOL_WIDTH} flexShrink={0}>
        {t('protocol')}
      </MetaDefault>
      <MetaDefault color="fg.muted" width={SOURCE_WIDTH} flexShrink={0}>
        {t('source')}
      </MetaDefault>
      <MetaDefault color="fg.muted" width={RECEIVED_WIDTH} flexShrink={0}>
        {t('received')}
      </MetaDefault>
      {showQuery ? (
        <MetaDefault color="fg.muted">{t('query')}</MetaDefault>
      ) : null}
    </HStack>
  );
}

function InteractionRow({
  interaction,
  defaultOpen,
}: {
  interaction: OobInteraction;
  defaultOpen?: boolean;
}) {
  const request = capturedRequest(interaction);

  return (
    <Collapsible.Root
      width="100%"
      defaultOpen={defaultOpen}
      disabled={request === undefined}
    >
      <Collapsible.Trigger asChild>
        <HStack
          gap={2}
          alignItems="center"
          width="100%"
          py={0.5}
          cursor={request === undefined ? 'default' : 'pointer'}
          _hover={request === undefined ? undefined : { bg: 'bg.muted' }}
        >
          <Box
            width={CHEVRON_WIDTH}
            flexShrink={0}
            display="flex"
            alignItems="center"
            color="fg.muted"
          >
            {request === undefined ? null : (
              <Collapsible.Context>
                {(collapsible) =>
                  collapsible.open ? (
                    <ArrowDown01Icon size={CHEVRON_SIZE} />
                  ) : (
                    <ArrowRight01Icon size={CHEVRON_SIZE} />
                  )
                }
              </Collapsible.Context>
            )}
          </Box>
          <Cell width={PROTOCOL_WIDTH}>
            {interaction.protocol.toUpperCase()}
          </Cell>
          <Cell width={SOURCE_WIDTH}>{interaction.remoteAddress}</Cell>
          <Cell width={RECEIVED_WIDTH}>
            {receivedAt(interaction.timestamp)}
          </Cell>
          {interaction.qType === undefined ? null : (
            <Cell>{interaction.qType}</Cell>
          )}
        </HStack>
      </Collapsible.Trigger>
      {request === undefined ? null : (
        <Collapsible.Content>
          <Box bg="bg.muted" borderRadius="sm" p={2} mt={1} mb={2}>
            <Transcript>{request}</Transcript>
          </Box>
        </Collapsible.Content>
      )}
    </Collapsible.Root>
  );
}

function Interactions({ interactions }: { interactions: OobInteraction[] }) {
  const hasQueryType = interactions.some(
    (interaction) => interaction.qType !== undefined,
  );

  return (
    <VStack gap={0} alignItems="stretch" width="100%">
      <ColumnHeaders showQuery={hasQueryType ? true : undefined} />
      {interactions.map((interaction) => (
        <InteractionRow
          key={interaction.id}
          interaction={interaction}
          defaultOpen={interactions.length === 1 ? true : undefined}
        />
      ))}
    </VStack>
  );
}

export function OobEvidenceLabel({ payload }: OobInteractionsCardProps) {
  const t = useTranslations('designSystem.artifacts.oobResult');

  return (
    <MetaDefault color="fg.muted" whiteSpace="nowrap">
      {payload.mode === '2'
        ? t('interactionCount', { count: payload.count })
        : t(`mode.${payload.mode}.name`)}
    </MetaDefault>
  );
}

export function OobInteractionsCard({ payload }: OobInteractionsCardProps) {
  const t = useTranslations('designSystem.artifacts.oobResult');

  if (payload.mode === '2') {
    return <Interactions interactions={payload.interactions} />;
  }

  return (
    <VStack gap={1} alignItems="stretch" width="100%">
      <MonoSmall display="block" color="fg.default" wordBreak="break-all">
        {payload.mode === '1' ? payload.domain : payload.dynamicUrl}
      </MonoSmall>
      <MetaDefault display="block" color="fg.muted">
        {t(`mode.${payload.mode}.meaning`)}
      </MetaDefault>
      {payload.mode === '1' ? <Transcript>{payload.message}</Transcript> : null}
    </VStack>
  );
}
